import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useLeague } from '../hooks/useLeague';
import { listLscArchive, LscApiError } from '../api';
import type { LscArchiveItem } from '../types';
import LeagueArchivesLibrary from '../components/archiveLibrary/LeagueArchivesLibrary';
import LoadingState from '../components/LoadingState';
import ErrorBanner from '../components/ErrorBanner';
import { TWITCH_VOD_EXPIRATION_COPY } from '../archiveCopy';
import { lscReturnTo } from '../routing';

const ARCHIVE_LIBRARY_FETCH_LIMIT = 100;

const LscArchiveListPage: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  return (
    <LscAuthGate returnTo={lscReturnTo(`/leagues/${leagueId}/archive`)}>
      <ArchiveListContent />
    </LscAuthGate>
  );
};

function ArchiveListContent() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { isCommissioner } = useLeague();
  const [searchParams] = useSearchParams();
  const matchFilter = searchParams.get('competition_match_id') ?? '';
  const [items, setItems] = useState<LscArchiveItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const params: {
          limit: number;
          group: 'match';
          competition_match_id?: string;
          archive_status?: string;
        } = {
          limit: ARCHIVE_LIBRARY_FETCH_LIMIT,
          group: 'match',
        };
        if (matchFilter) params.competition_match_id = matchFilter;
        if (isCommissioner && statusFilter) {
          params.archive_status = statusFilter;
        } else if (!isCommissioner) {
          params.archive_status = 'published';
        }
        const data = await listLscArchive(leagueId, params);
        if (!cancelled) setItems(data);
      } catch (err) {
        if (!cancelled) {
          if (err instanceof LscApiError && err.status === 403) {
            setError('You do not have access to this archive.');
          } else {
            setError(err instanceof LscApiError ? err.message : 'Could not load archive.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueId, matchFilter, isCommissioner, statusFilter]);

  if (loading) return <LoadingState message="Loading archives..." />;

  return (
    <div className="space-y-6" data-testid="lsc-archive-list-page">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">League Archives</h1>
        <p className="text-sm text-secondary-400">
          Search and watch published league replays by week, team, matchup, or broadcaster.
        </p>
        <p className="text-xs text-secondary-500 mt-2" data-testid="twitch-vod-expiration-copy">
          {TWITCH_VOD_EXPIRATION_COPY}
        </p>
      </div>

      {error && <ErrorBanner message={error} />}

      <LeagueArchivesLibrary
        leagueId={leagueId!}
        items={items}
        isCommissioner={isCommissioner}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        matchFilter={matchFilter}
      />
    </div>
  );
}

export default LscArchiveListPage;
